/**
 * Centralized API error handler for authentication, authorization, and impersonation scenarios.
 *
 * This module is intentionally separate from error-utils.ts to avoid circular dependencies
 * (error-utils.ts ← auth.ts, but this file needs auth.ts).
 */

import { isSkippableError } from '@/utils/error-utils';
import { isForbiddenError, isTokenRelatedAuthError } from '@/utils/isAuthError';
import { showImpersonationErrorToast } from '@/utils/permissions-widget/impersonation-error-toast';
import { isImpersonating } from '@/utils/permissions-widget/token';

/**
 * Centralized API error handler for authentication, authorization, and impersonation scenarios.
 *
 * Handles the following error types:
 * - **Skippable errors** (AbortError, network offline): Returns false
 * - **During impersonation (401/403/Okta errors)**: Shows toast and returns true
 * - **API 401/403 (not impersonating)**: Returns false (API-level issue, let error propagate)
 * - **Okta SDK errors (not impersonating)**: Logs out and throws (actual auth session problem)
 *
 * @param err - The error to handle
 * @param source - Label for logging (e.g., 'getApiToken', 'Interceptor')
 * @returns true if the error was handled (toast shown), false if not handled
 * @throws For Okta SDK errors when not impersonating (after logout)
 *
 * @example
 * ```ts
 * const wasHandled = await handleApiError(err, 'fetchData');
 * if (!wasHandled) {
 *   // Error needs to propagate
 *   throw err;
 * }
 * ```
 */
export const handleApiError = async (
  err: unknown,
  source: string,
): Promise<boolean> => {
  // Skip handling for aborted requests, network errors, etc.
  if (isSkippableError(err)) {
    return false;
  }

  const isCurrentlyImpersonating = isImpersonating();

  // During impersonation, show toast for auth-related errors (401/403/Okta)
  if (isCurrentlyImpersonating) {
    if (isForbiddenError(err)) {
      console.warn(`[${source}] Forbidden error during impersonation:`, err);
      showImpersonationErrorToast(err);
      return true;
    }

    if (isTokenRelatedAuthError(err)) {
      console.warn(`[${source}] Auth error during impersonation:`, err);
      showImpersonationErrorToast(err);
      return true;
    }
  }

  // API 401/403 when not impersonating: don't logout, just let error propagate
  // (API might reject for reasons other than invalid token)
  return false;
};
